// The site's questions to the database (CLAUDE.md: the data layer).
//
// **The browser never reads a puzzle table.** It calls named functions that
// answer with what a player has earned and nothing more (migration 0054 and
// after), over Supabase's REST endpoint with the publishable key -- a plain
// fetch, because the whole client library is a lot of code for two POSTs.

const URL = import.meta.env.VITE_SUPABASE_URL as string;
const KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

/**
 * **The test code**, present only in the development server and Cloudflare
 * preview builds (vite.config.ts). With it, queued puzzles can be played before
 * launch, and every screen says so.
 */
export const TEST_CODE: string | null = (import.meta.env.VITE_TEST_CODE as string) || null;
export const TEST_MODE = TEST_CODE !== null;

export class ApiError extends Error {}

export async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  if (!URL || !KEY) throw new ApiError('The site is not connected to its database.');
  let res: Response;
  try {
    res = await fetch(`${URL}/rest/v1/rpc/${fn}`, {
      method: 'POST',
      // **No cookies, either way.** Supabase sits behind Cloudflare, whose bot
      // check answers with a `__cf_bm` cookie; `omit` means the browser neither
      // sends a cookie nor keeps one, so "Cinemore doesn't use cookies" stays
      // true whatever the API's edge decides to send (the Privacy page).
      credentials: 'omit',
      headers: { apikey: KEY, 'content-type': 'application/json' },
      body: JSON.stringify(TEST_CODE ? { ...args, p_test: TEST_CODE } : args)
    });
  } catch {
    throw new ApiError('Could not reach the server. Check your connection and try again.');
  }
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const message = body && typeof body === 'object' && 'message' in body ? String(body.message) : res.statusText;
    throw new ApiError(message);
  }
  return body as T;
}
